import React from "react";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Menu, MenuItem, StyledEngineProvider, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";
import { CalendarToday } from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterLuxon } from "@mui/x-date-pickers-pro/AdapterLuxon";
import { DateTime } from "luxon";
import { PowerBiDateRangeStyles } from "../../styles";
import { usePowerBiDateRanges } from "../../hooks";

const PowerBiDateRanges = (): React.JSX.Element => {
  const theme = useTheme();
  const classes = PowerBiDateRangeStyles(theme);

  const {
    currentInterval,
    showDateRange,
    setShowDateRange,
    dateTo,
    setDateTo,
    dateFrom,
    setDateFrom,
    intervals,
    dateFilterViews,
    handleIntervalChange,
    handleResetDates,
    handleSetDates,
    handleMenu,
    handleClose,
    anchorEl,
  } = usePowerBiDateRanges();

  return (
    <>
      <Button
        variant="outlined"
        color="inherit"
        startIcon={<CalendarToday />}
        onClick={handleMenu}
        style={{ marginLeft: 10 }}
      >
        {currentInterval?.value ? currentInterval.value : "DATE RANGE"}
      </Button>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        keepMounted
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {intervals.map((i) => (
          <MenuItem
            key={i.value}
            onClick={() =>
              i.value === "Custom Range"
                ? setShowDateRange(true)
                : handleIntervalChange(i)
            }
          >
            {i.value}
          </MenuItem>
        ))}
      </Menu>

      {showDateRange && (
        <Dialog
          open={showDateRange}
          onClose={() => setShowDateRange(false)}
          maxWidth={"sm"}
        >
          <DialogTitle>Custom Range</DialogTitle>
          <DialogContent dividers>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <StyledEngineProvider injectFirst>
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <DatePicker
                      slotProps={{
                        actionBar: {
                          actions: ["clear"],
                        },
                        textField: {
                          id: "start-date",
                          variant: "outlined",
                          InputLabelProps: { shrink: true },
                        },
                      }}
                      views={dateFilterViews}
                      minDate={DateTime.utc()
                        .minus({ years: 1 })
                        .startOf("month")}
                      maxDate={DateTime.utc().startOf("month")}
                      sx={classes.datePicker}
                      label={
                        <>
                          <FilterListIcon sx={classes.menuIcon} />
                          Start Date
                        </>
                      }
                      value={dateFrom}
                      onChange={(date) => {
                        if (date) {
                          setDateFrom(date.startOf("month"));
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={12}>
                    <DatePicker
                      slotProps={{
                        actionBar: {
                          actions: ["clear"],
                        },
                        textField: {
                          id: "end-date",
                          variant: "outlined",
                          InputLabelProps: { shrink: true },
                        },
                      }}
                      views={dateFilterViews}
                      minDate={DateTime.utc()
                        .minus({ years: 1 })
                        .endOf("month")}
                      maxDate={DateTime.utc().endOf("month")}
                      sx={classes.datePicker}
                      label={
                        <>
                          <FilterListIcon sx={classes.menuIcon} />
                          End Date
                        </>
                      }
                      value={dateTo}
                      onChange={(date) => {
                        if (date) {
                          setDateTo(date.endOf("month"));
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </StyledEngineProvider>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions sx={classes.padding}>
            <Grid container justifyContent={"space-evenly"}>
              <Grid>
                <Button
                  variant={"contained"}
                  color={"secondary"}
                  onClick={handleResetDates}
                >
                  Reset
                </Button>
              </Grid>
              <Grid>
                <Button
                  variant={"contained"}
                  color={"primary"}
                  onClick={handleSetDates}
                >
                  Set
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default PowerBiDateRanges;
